<div class="mb-6 flex flex-wrap items-center justify-between gap-4">
  <div>
    <h1 class="!mt-0 !mb-2">Materials</h1>
    <p class="!mt-0 text-muted-color">Manage material options for quoting.</p>
  </div>
  <p-button label="New Material" icon="pi pi-plus" (onClick)="openDrawer()" />
</div>

<div class="flex flex-wrap justify-between items-center mb-4">
  <div class="flex items-center gap-3">
    <div class="flex items-center gap-2">
      <p-checkbox
        [formControl]="includeArchived"
        [binary]="true"
        inputId="includeArchived"
      />
      <label for="includeArchived" class="text-sm font-medium"
        >Include Archived Materials</label
      >
    </div>
  </div>
</div>

@let materials = tableMaterials$ | async;
@let currencyCode = currencyCode$ | async;
<div class="rounded-xl border border-surface-switch-200 shadow-sm">
  <!-- Header Row -->
  <div
    class="hidden lg:grid rounded-t-xl bg-surface-switch-100 items-center gap-4 px-4 py-3 text-sm font-bold text-surface-switch-600 uppercase"
    style="grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1.5fr 0.8fr 1fr"
  >
    <div>Name</div>
    <div>Price</div>
    <div>Price Type</div>
    <div>Sheet Size (mm)</div>
    <div>Density (kg/m³)</div>
    <div>Description</div>
    <div class="text-center">Attachments</div>
    <div class="text-center">Actions</div>
  </div>

  @if (materials && materials.length > 0) {
    <div class="flex flex-col">
      @for (material of materials; track material.id) {
        <div
          class="border-b border-surface-switch-200 last:border-b-0 bg-surface-switch-50 transition-colors duration-200 hover:bg-surface-switch-100"
        >
          <!-- Mobile Layout -->
          <div class="lg:hidden p-4 flex flex-col gap-2">
            <div class="flex justify-between items-start">
              <div>
                <span class="font-semibold text-lg">{{ material.name }}</span>
                @if (material.is_archived) {
                  <p-chip label="Archived" styleClass="ml-2 text-xs" />
                }
              </div>
              <div class="flex gap-1 shrink-0 ml-2">
                @if (!material.is_archived) {
                  <p-button
                    icon="pi pi-pencil"
                    severity="secondary"
                    size="small"
                    text="true"
                    (onClick)="openDrawer(material)"
                    pTooltip="Edit"
                    tooltipPosition="top"
                  ></p-button>
                  <p-button
                    icon="pi pi-box"
                    severity="secondary"
                    size="small"
                    text="true"
                    (click)="openConfirm($event, material, 'archive')"
                    pTooltip="Archive"
                    tooltipPosition="top"
                  ></p-button>
                } @else {
                  <p-button
                    icon="pi pi-refresh"
                    severity="secondary"
                    size="small"
                    text="true"
                    (click)="openConfirm($event, material, 'unarchive')"
                    pTooltip="Unarchive"
                    tooltipPosition="top"
                  ></p-button>
                }
              </div>
            </div>
            <div class="text-sm font-medium">
              {{
                +material.material_price
                  | currency: currencyCode ?? "" : "symbol" : "1.0-9"
              }}{{
                material.material_price_type === "volume"
                  ? "/m³"
                  : material.material_price_type === "volume_l"
                    ? "/L"
                    : "/m²"
              }}
              <span class="text-muted-color ml-1"
                >({{ getPriceTypeLabel(material.material_price_type) }})</span
              >
            </div>
            <div class="flex items-center gap-2 text-sm text-muted-color">
              <span
                >Updated:
                {{ formatPriceUpdateDate(material.price_updated_at) }}</span
              >
              @if (isPriceStale(material)) {
                <i
                  class="pi pi-info-circle text-surface-switch-600"
                  pTooltip="Material price has not been updated in over 2 weeks"
                ></i>
              }
            </div>
            @if (material.sheet_sizes && material.sheet_sizes.length > 0) {
              <div class="text-sm text-muted-color">
                Sheet:
                @for (s of material.sheet_sizes; track $index) {
                  {{ s.x }} × {{ s.y }} × {{ s.z
                  }}{{ $index < material.sheet_sizes.length - 1 ? "; " : "" }}
                }
              </div>
            }
            @if (material.density_kilogram_per_m3) {
              <div class="text-sm text-muted-color">
                Density:
                {{ +material.density_kilogram_per_m3 | number: "1.0-9" }} kg/m³
              </div>
            }
            @if (material.description) {
              <div class="text-sm text-muted-color line-clamp-2">
                {{ material.description }}
              </div>
            }
            @if (getAttachmentCount(material) > 0) {
              <p-button
                icon="pi pi-paperclip"
                [label]="getAttachmentCount(material).toString()"
                severity="secondary"
                size="small"
                text="true"
                (onClick)="openAttachmentsModal(material)"
                pTooltip="View attachments"
                tooltipPosition="top"
              ></p-button>
            }
          </div>

          <!-- Desktop Layout -->
          <div
            class="hidden lg:grid lg:items-center lg:gap-4 lg:px-4 lg:py-3"
            style="grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1.5fr 0.8fr 1fr"
          >
            <div>
              <span class="font-medium">{{ material.name }}</span>
              @if (material.is_archived) {
                <p-chip label="Archived" styleClass="ml-2 text-xs" />
              }
            </div>
            <div class="text-sm">
              {{
                +material.material_price
                  | currency: currencyCode ?? "" : "symbol" : "1.0-9"
              }}{{
                material.material_price_type === "volume"
                  ? "/m³"
                  : material.material_price_type === "volume_l"
                    ? "/L"
                    : "/m²"
              }}
              @if (isPriceStale(material)) {
                <i
                  class="pi pi-info-circle text-surface-switch-600 ml-1"
                  pTooltip="Price not updated in over 2 weeks"
                ></i>
              }
            </div>
            <div class="text-sm">
              {{ getPriceTypeLabel(material.material_price_type) }}
            </div>
            <div class="text-sm">
              @if (material.sheet_sizes && material.sheet_sizes.length > 0) {
                @for (s of material.sheet_sizes; track $index) {
                  {{ s.x }} × {{ s.y }} × {{ s.z
                  }}{{ $index < material.sheet_sizes.length - 1 ? "; " : "" }}
                }
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm">
              @if (material.density_kilogram_per_m3) {
                {{ +material.density_kilogram_per_m3 | number: "1.0-9" }}
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="text-sm">
              @if (material.description) {
                <p class="line-clamp-2 break-words">
                  {{ material.description }}
                </p>
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="flex justify-center">
              @if (getAttachmentCount(material) > 0) {
                <p-button
                  icon="pi pi-paperclip"
                  [label]="getAttachmentCount(material).toString()"
                  severity="secondary"
                  size="small"
                  text="true"
                  (onClick)="openAttachmentsModal(material)"
                  pTooltip="View attachments"
                  tooltipPosition="top"
                ></p-button>
              } @else {
                <span class="text-muted-color">—</span>
              }
            </div>
            <div class="flex justify-center gap-1">
              @if (!material.is_archived) {
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  size="small"
                  text="true"
                  (onClick)="openDrawer(material)"
                  pTooltip="Edit"
                  tooltipPosition="top"
                ></p-button>
                <p-button
                  icon="pi pi-box"
                  severity="secondary"
                  size="small"
                  text="true"
                  (click)="openConfirm($event, material, 'archive')"
                  pTooltip="Archive"
                  tooltipPosition="top"
                ></p-button>
              } @else {
                <p-button
                  icon="pi pi-refresh"
                  severity="secondary"
                  size="small"
                  text="true"
                  (click)="openConfirm($event, material, 'unarchive')"
                  pTooltip="Unarchive"
                  tooltipPosition="top"
                ></p-button>
              }
            </div>
          </div>
        </div>
      }
    </div>
  } @else {
    <div class="text-center text-muted-color py-8">
      <i class="pi pi-inbox text-4xl mb-4 block"></i>
      <p>No materials found</p>
    </div>
  }
</div>

<shared-confirm-dialog />

<p-drawer
  [(visible)]="isDrawerVisible"
  [header]="materialInDraw ? 'Edit Material' : 'Add Material'"
  position="right"
  [styleClass]="'!w-full lg:!w-3/4 2xl:!w-1/2'"
>
  <form [formGroup]="materialFormGroup" class="flex flex-col gap-6">
    <!-- Name -->
    <div class="pt-1">
      <p-floatlabel variant="on">
        <input pInputText formControlName="name" type="text" class="w-full" />
        <label>Name</label>
      </p-floatlabel>
    </div>

    <!-- Price and Price Type -->
    <div class="flex flex-col gap-4">
      <div>
        <p-floatlabel variant="on">
          <p-inputNumber
            formControlName="price"
            [mode]="'currency'"
            [currency]="currencyCode || 'GBP'"
            locale="en-US"
            [min]="0"
            [minFractionDigits]="2"
            [maxFractionDigits]="9"
            [suffix]="'/' + materialPriceSuperscript"
            class="w-full"
          >
          </p-inputNumber>
          <label>Material Price ({{ materialPriceSuperscript }})</label>
        </p-floatlabel>
      </div>

      <div>
        <label class="block mb-2 text-sm font-medium">Price Type</label>
        <p-select
          [options]="priceTypeOptions"
          formControlName="priceType"
          optionLabel="label"
          optionValue="value"
          class="w-full"
        />
      </div>
    </div>

    <div>
      <label class="block mb-2 text-sm font-medium" for="materialDescription">
        Description
      </label>
      <textarea
        pTextarea
        id="materialDescription"
        formControlName="description"
        class="w-full"
        rows="3"
      ></textarea>
      <p class="text-sm text-muted-color mt-2">
        Add any extra info here about the material, including supplier details,
        required purchase sizes, lead times, etc.
      </p>
    </div>

    <p-divider class="my-1!" />

    <!-- Density -->
    <div>
      <p-floatlabel variant="on">
        <p-inputNumber
          formControlName="densityGramsPerm3"
          [mode]="'decimal'"
          [min]="0"
          [minFractionDigits]="2"
          [maxFractionDigits]="9"
          class="w-full"
        >
        </p-inputNumber>
        <label>Density (kg/m³)</label>
      </p-floatlabel>
      <p class="text-sm text-muted-color mt-2">
        Used to derive CNC machining time.
      </p>
    </div>

    <div>
      <p-floatlabel variant="on">
        <p-inputNumber
          formControlName="surfaceHardness"
          [mode]="'decimal'"
          [min]="1"
          [max]="10000"
          [maxFractionDigits]="0"
          [useGrouping]="false"
          class="w-full"
        >
        </p-inputNumber>
        <label>Surface Hardness (HV)</label>
      </p-floatlabel>
      <p class="text-sm text-muted-color mt-2">
        Used to derive finishing time.
      </p>
    </div>

    <div>
      <p-floatlabel variant="on">
        <p-inputNumber
          formControlName="curedThicknessMm"
          [mode]="'decimal'"
          [min]="0.001"
          [minFractionDigits]="0"
          [maxFractionDigits]="3"
          [useGrouping]="false"
          class="w-full"
        >
        </p-inputNumber>
        <label>Cured Thickness (mm)</label>
      </p-floatlabel>
      <p class="text-sm text-muted-color mt-2">Used to derive ply count.</p>
    </div>

    <!-- Attachments Upload -->
    <div>
      <label class="block mb-2 text-sm font-medium">Attachments</label>
      <p class="text-sm text-muted-color mb-3">
        Upload data sheets or other relevant documentation for this material.
      </p>

      @if (
        (materialInDraw &&
          materialInDraw.attachments &&
          materialInDraw.attachments.length > 0) ||
        pendingFiles.length > 0
      ) {
        <div class="flex flex-col gap-2 mb-3">
          <!-- Existing attachments -->
          @if (materialInDraw && materialInDraw.attachments) {
            @for (
              attachment of materialInDraw.attachments;
              track attachment.id
            ) {
              @if (!isAttachmentDeleted(attachment.id)) {
                <div
                  class="flex items-center gap-3 p-3 bg-surface-switch-100 border border-surface-switch-300 rounded-lg"
                >
                  <i class="pi pi-file text-xl"></i>
                  <span class="flex-1 truncate">{{
                    attachment.original_filename
                  }}</span>
                  <p-button
                    icon="pi pi-times"
                    severity="secondary"
                    size="small"
                    text="true"
                    (onClick)="removeAttachment(attachment)"
                  ></p-button>
                </div>
              }
            }
          }

          <!-- Pending files -->
          @for (file of pendingFiles; track $index) {
            <div
              class="flex items-center gap-3 p-3 bg-surface-switch-100 rounded-lg border border-surface-switch-300"
            >
              <i class="pi pi-file text-xl"></i>
              <span class="flex-1 truncate">{{ file.name }}</span>
              <p-button
                icon="pi pi-times"
                severity="secondary"
                size="small"
                text="true"
                (onClick)="removePendingFile($index)"
              ></p-button>
            </div>
          }
        </div>
      }

      <p-fileupload
        mode="basic"
        [chooseLabel]="'Add File'"
        [auto]="true"
        [multiple]="true"
        [maxFileSize]="this.MAX_FILE_SIZE"
        invalidFileSizeMessageDetail="the maximum allowed file size is 50 MB."
        (onSelect)="onAttachmentSelect($event)"
      ></p-fileupload>
    </div>

    @if (isSuperuser) {
      <div class="border-t border-surface-switch-200">
        <h3 class="text-xs font-semibold tracking-wide text-surface-switch-900">
          Superuser-only options
        </h3>
        <p class="text-sm text-muted-color mt-1">
          Configure manual pricing-time overrides for this material.
        </p>
      </div>

      <div>
        <p-floatlabel variant="on">
          <p-inputNumber
            inputId="cncTimeMultiplier"
            formControlName="cncTimeMultiplier"
            [minFractionDigits]="2"
            [maxFractionDigits]="3"
            [min]="0.1"
            [max]="20"
            class="w-full"
          >
          </p-inputNumber>
          <label for="cncTimeMultiplier">CNC Time Multiplier (override)</label>
        </p-floatlabel>
        <p class="text-sm text-muted-color mt-2">
          Optional manual override. Leave empty to use the density-derived
          multiplier.
        </p>
      </div>

      <div>
        <p-floatlabel variant="on">
          <p-inputNumber
            inputId="compositeTimeMultiplier"
            formControlName="compositeTimeMultiplier"
            [minFractionDigits]="2"
            [maxFractionDigits]="3"
            [min]="0.1"
            [max]="20"
            class="w-full"
          >
          </p-inputNumber>
          <label for="compositeTimeMultiplier">Composite Time Multiplier</label>
        </p-floatlabel>
        <p class="text-sm text-muted-color mt-2">
          Multiplies baseline composite layup and handling time for this
          material.
        </p>
      </div>

      <div>
        <p-floatlabel variant="on">
          <p-inputNumber
            inputId="finishingTimeMultiplier"
            formControlName="finishingTimeMultiplier"
            [minFractionDigits]="2"
            [maxFractionDigits]="3"
            [min]="0.1"
            [max]="20"
            class="w-full"
          >
          </p-inputNumber>
          <label for="finishingTimeMultiplier"
            >Finishing Time Multiplier (override)</label
          >
        </p-floatlabel>
        <p class="text-sm text-muted-color mt-2">
          Optional manual override. Leave empty to use the hardness-derived
          multiplier.
        </p>
      </div>
    }

    <div>
      <div class="mb-6">
        <div
          class="flex gap-1.5 items-center border-t border-surface-switch-200"
        >
          <h3
            class="text-xs font-semibold tracking-wide text-surface-switch-900"
          >
            DFM Checks
          </h3>
          <i
            class="pi pi-info-circle !text-xl text-muted-color cursor-pointer transform translate-y-1"
            pTooltip="Analyse uploaded parts against material-specific manufacturing rules. These checks help identify features that may be difficult or impossible to manufacture."
            tooltipPosition="right"
            aria-label="About DFM checks"
          ></i>
        </div>
        <p class="text-sm text-muted-color">
          Add & Configure DFM Checks for
          {{
            materialInDraw
              ? materialFormGroup.get("name")?.value
              : "this material"
          }}.
        </p>
      </div>

      <!-- DFM Checks Toggles -->
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label for="countHoles">Count Holes</label>
            <i
              class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
              [pTooltip]="dfmDefinitions.holes_count.tooltip"
            ></i>
          </div>
          <p-toggleswitch formControlName="countHoles" inputId="countHoles" />
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label for="unmachinableHoles">Unmachinable Holes</label>
            <i
              class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
              [pTooltip]="dfmDefinitions.unmachinable_holes.tooltip"
            ></i>
          </div>
          <p-toggleswitch
            formControlName="unmachinableHoles"
            inputId="unmachinableHoles"
          />
        </div>

        <!-- Unmachinable Holes Parameters -->
        @if (materialFormGroup.get("unmachinableHoles")?.value) {
          <div class="flex flex-col gap-3 pl-4 border-l-2 border-surface">
            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Drill Head Radius</label>
              <p-inputNumber
                formControlName="drillHeadRadius"
                [mode]="'decimal'"
                [min]="0"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
                suffix=" mm"
              >
              </p-inputNumber>
            </div>

            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Drill Ratio (Blind)</label>
              <p-inputNumber
                formControlName="drillBitRatioBlind"
                [mode]="'decimal'"
                [min]="0"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
                suffix=" L/D"
              >
              </p-inputNumber>
            </div>

            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Drill Ratio (Through)</label>
              <p-inputNumber
                formControlName="drillBitRatioThrough"
                [mode]="'decimal'"
                [min]="0"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
                suffix=" L/D"
              >
              </p-inputNumber>
            </div>
          </div>
        }

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label for="thinWalls">Thin Walls</label>
            <i
              class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
              [pTooltip]="dfmDefinitions.thin_walls.tooltip"
            ></i>
          </div>
          <p-toggleswitch formControlName="thinWalls" inputId="thinWalls" />
        </div>

        <!-- Thin Walls Parameters -->
        @if (materialFormGroup.get("thinWalls")?.value) {
          <div class="flex flex-col gap-3 pl-4 border-l-2 border-surface">
            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Minimum Thickness</label>
              <p-inputNumber
                formControlName="minThickness"
                [mode]="'decimal'"
                [min]="0"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
              >
              </p-inputNumber>
            </div>

            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Maximum Thickness</label>
              <p-inputNumber
                formControlName="maxThickness"
                [mode]="'decimal'"
                [min]="0"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
              >
              </p-inputNumber>
            </div>
          </div>
        }

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label for="narrowGaps">Narrow Gaps</label>
            <i
              class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
              [pTooltip]="dfmDefinitions.narrow_gaps.tooltip"
            ></i>
          </div>
          <p-toggleswitch formControlName="narrowGaps" inputId="narrowGaps" />
        </div>

        <!-- Narrow Gaps Parameters -->
        @if (materialFormGroup.get("narrowGaps")?.value) {
          <div class="flex flex-col gap-3 pl-4 border-l-2 border-surface">
            <div class="flex items-center gap-2">
              <label class="text-sm w-36">Angle Tolerance</label>
              <p-inputNumber
                formControlName="angleTolerance"
                [mode]="'decimal'"
                [min]="0"
                [max]="180"
                [minFractionDigits]="1"
                [maxFractionDigits]="2"
                [style]="{ width: '60px' }"
              >
              </p-inputNumber>
            </div>
          </div>
        }

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <label for="internalVolume">Internal Volume</label>
            <i
              class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
              [pTooltip]="dfmDefinitions.internal_volume.tooltip"
            ></i>
          </div>

          <p-toggleswitch
            formControlName="internalVolume"
            inputId="internalVolume"
          />
        </div>

        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <label for="blockingUp">Blocking Up</label>
              <i
                class="pi pi-info-circle text-sm text-muted-color cursor-pointer"
                [pTooltip]="dfmDefinitions.blocking_up.tooltip"
              ></i>
            </div>
            <p-toggleswitch formControlName="blockingUp" inputId="blockingUp" />
          </div>

          <p class="text-sm text-muted-color mt-1">
            Add as many sheet sizes as needed.
            @if (materialFormGroup.get("blockingUp")?.value) {
              Blocking up requires at least one.
            }
          </p>

          @if (materialFormGroup.get("blockingUp")?.value) {
            <div class="flex flex-col gap-3 mt-2">
              @for (row of sheetSizeRows; track $index) {
                <div class="flex items-end gap-2 flex-wrap">
                  <div class="grid grid-cols-3 gap-2 flex-1 min-w-[200px]">
                    <div class="w-full">
                      <p-floatlabel variant="on">
                        <p-inputNumber
                          [formControl]="row.controls.x"
                          [min]="minSheetDimensionMm"
                          [maxFractionDigits]="0"
                          class="w-full"
                          inputStyleClass="w-full"
                          [invalid]="isInvalid('sheetSizes.' + $index + '.x')"
                        />
                        <label>X (mm)</label>
                      </p-floatlabel>
                    </div>
                    <div class="w-full">
                      <p-floatlabel variant="on">
                        <p-inputNumber
                          [formControl]="row.controls.y"
                          [min]="minSheetDimensionMm"
                          [maxFractionDigits]="0"
                          class="w-full"
                          inputStyleClass="w-full"
                          [invalid]="isInvalid('sheetSizes.' + $index + '.y')"
                        />
                        <label>Y (mm)</label>
                      </p-floatlabel>
                    </div>
                    <div class="w-full">
                      <p-floatlabel variant="on">
                        <p-inputNumber
                          [formControl]="row.controls.z"
                          [min]="minSheetDimensionMm"
                          [maxFractionDigits]="0"
                          class="w-full"
                          inputStyleClass="w-full"
                          [invalid]="isInvalid('sheetSizes.' + $index + '.z')"
                        />
                        <label>Z (mm)</label>
                      </p-floatlabel>
                    </div>
                  </div>
                  <p-button
                    icon="pi pi-trash"
                    severity="secondary"
                    [rounded]="true"
                    (onClick)="removeSheetSizeRow($index)"
                    [disabled]="sheetSizesArray.length <= 1"
                    pTooltip="Remove sheet size"
                  />
                </div>
              }
              <p-button
                label="Add sheet size"
                icon="pi pi-plus"
                (onClick)="addSheetSizeRow()"
              />
            </div>
          }
        </div>
      </div>
    </div>

    <div class="flex justify-end gap-2 pt-4">
      @if (saveError) {
        <p-message severity="error" styleClass="w-full mb-2">
          {{ saveError }}
        </p-message>
      }
      <p-button label="Cancel" severity="secondary" (onClick)="closeDrawer()" />
      <p-button
        label="Save"
        icon="pi pi-save"
        (onClick)="saveMaterial()"
        [disabled]="!materialFormGroup.valid || !materialFormGroup.dirty"
      />
    </div>
  </form>
</p-drawer>

<!-- Attachments Modal -->
<p-dialog
  [(visible)]="isAttachmentsModalVisible"
  [header]="'Attachments for ' + (modalMaterial?.name || '')"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '50vw', minWidth: '400px' }"
>
  @if (
    modalMaterial &&
    modalMaterial.attachments &&
    modalMaterial.attachments.length > 0
  ) {
    <div class="flex flex-col gap-3">
      @for (attachment of modalMaterial.attachments; track attachment.id) {
        <div
          class="flex items-center gap-3 p-3 bg-surface-switch-100 rounded-lg"
        >
          <i class="pi pi-file text-xl"></i>
          <span class="flex-1 truncate">{{
            attachment.original_filename
          }}</span>
          <p-button
            icon="pi pi-download"
            severity="secondary"
            size="small"
            text="true"
            pTooltip="Download"
            (onClick)="downloadAttachment(attachment)"
          ></p-button>
        </div>
      }
    </div>
  } @else {
    <div class="text-center py-8 text-muted-color">
      <i class="pi pi-inbox text-4xl mb-4 block"></i>
      <p>No attachments for this material</p>
    </div>
  }
</p-dialog>
